import * as React from "react";
import { REDUCED_MOTION_QUERY } from "../../../shared/hooks/usePrefersReducedMotion";
import { WidthMorph } from "../../../shared/hooks/useWidthMorph";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaSwap from "../kuika-swap/kuika-swap";

/**
 * Where a button's action stands. The names are `run-button.tsx`'s preview states.
 * `idle` shows the button's own label; the others replace it with a label of their own.
 */
export type KuikaButtonStatus = "idle" | "loading" | "success" | "error";

/** Overrides for the words a status shows. The ui-kit has no i18n, so the defaults are English. */
export interface IKuikaButtonStatusLabels {
  loading?: string;
  success?: string;
  error?: string;
}

/** How long a caller keeps `success` up before it closes the surface or returns to `idle`. */
export const KUIKA_BUTTON_SUCCESS_HOLD_MS: number = 700;

/** How long a caller keeps `error` up before it returns to `idle`. */
export const KUIKA_BUTTON_ERROR_HOLD_MS: number = 1600;

const DEFAULT_STATUS_LABELS: Required<IKuikaButtonStatusLabels> = {
  loading: "Saving",
  success: "Saved",
  error: "Try again"
};

interface IKuikaButtonStatusContentProps {
  status: KuikaButtonStatus;
  labels?: IKuikaButtonStatusLabels;
  /** Glyph size for the rung, in px — KuikaSvg takes its size from props. */
  iconSize: number;
  /** The button's own label, shown while `idle`. */
  children: React.ReactNode;
}

/**
 * The inside of a button that has a `status`: the label rolls to the status's own label, and the
 * button's width follows it instead of jumping.
 *
 * The width is measured once per status change and never observed, by `WidthMorph`
 * (`shared/hooks/useWidthMorph.ts`, shared with KuikaBadge). `getSnapshotBeforeUpdate` reads the
 * width before React changes the label — the one measurement a hook could not take, and the reason
 * this is a class; after the change the new natural width is read, the button is pinned to the old
 * one, and released to the new one so `width` can transition (`.kuika-button--status`). When the
 * transition ends the inline width is removed, so the button is sized by its content again. Reduced
 * motion skips the pin, and the width changes at once.
 *
 * The outgoing label is laid over the incoming one (`KuikaSwap leaving="overlay"`), so the natural
 * width is the new label's alone however long the old one takes to leave.
 */
export class KuikaButtonStatusContent extends React.Component<IKuikaButtonStatusContentProps, object, number | null> {
  private readonly rootRef: React.RefObject<HTMLSpanElement> = React.createRef<HTMLSpanElement>();
  private readonly widthMorph: WidthMorph = new WidthMorph();

  getSnapshotBeforeUpdate(prevProps: Readonly<IKuikaButtonStatusContentProps>): number | null {
    if (prevProps.status === this.props.status) {
      return null;
    }

    const button: HTMLElement | null = this.getButton();
    return button === null ? null : button.offsetWidth;
  }

  componentDidUpdate(
    _prevProps: Readonly<IKuikaButtonStatusContentProps>,
    _prevState: Readonly<object>,
    fromWidth: number | null
  ): void {
    if (fromWidth !== null) {
      this.morphWidth(fromWidth);
    }
  }

  componentWillUnmount(): void {
    this.widthMorph.settle();
  }

  private getButton(): HTMLElement | null {
    const root: HTMLSpanElement | null = this.rootRef.current;
    return root === null ? null : root.closest<HTMLElement>(".kuika-button");
  }

  private morphWidth(fromWidth: number): void {
    // A status change during a morph continues from wherever the width is now — the snapshot above
    // read it — so the running morph lets go of the width before the new one is measured.
    this.widthMorph.settle();

    const button: HTMLElement | null = this.getButton();
    if (button === null) {
      return;
    }

    // A width the caller set inline is not ours to animate.
    if (button.style.width !== "") {
      return;
    }

    const prefersReducedMotion: boolean =
      typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_QUERY).matches;
    if (prefersReducedMotion) {
      return;
    }

    const toWidth: number = button.offsetWidth;
    if (toWidth === fromWidth) {
      return;
    }

    this.widthMorph.start(button, fromWidth, toWidth);
  }

  private renderStatusBody(): React.ReactNode {
    const { status, labels, iconSize, children } = this.props;
    const words: Required<IKuikaButtonStatusLabels> = {
      loading: labels?.loading ?? DEFAULT_STATUS_LABELS.loading,
      success: labels?.success ?? DEFAULT_STATUS_LABELS.success,
      error: labels?.error ?? DEFAULT_STATUS_LABELS.error
    };

    if (status === "loading") {
      return (
        <>
          <span className="kuika-button__spinner" aria-hidden="true" />
          <span>{words.loading}</span>
        </>
      );
    }

    if (status === "success") {
      return (
        <>
          <KuikaSvg name="check" width={iconSize} height={iconSize} aria-hidden="true" />
          <span>{words.success}</span>
        </>
      );
    }

    if (status === "error") {
      return (
        <>
          <KuikaSvg name="circle-x-2" width={iconSize} height={iconSize} aria-hidden="true" />
          <span>{words.error}</span>
        </>
      );
    }

    return children;
  }

  render(): React.ReactNode {
    return (
      <span ref={this.rootRef} className="kuika-button__status" aria-live="polite">
        <KuikaSwap contentKey={this.props.status} variant="roll" leaving="overlay">
          <span className="kuika-button__status-body">{this.renderStatusBody()}</span>
        </KuikaSwap>
      </span>
    );
  }
}
